import { ChangeDetectionStrategy, Component, input, computed, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceAction, DeviceActionType } from 'src/api/device-action';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '../../../services/cloud-api.service';

interface StageState {
    icon: string;
    label: string;
    details: string;
    state: 'completed' | 'active' | 'pending';
    color?: 'success' | 'error' | 'warning';
}

@Component({
    selector: 'app-action-item',
    standalone: true,
    imports: [CommonModule, ButtonModule, DialogModule],
    templateUrl: './action-item.component.html',
    styleUrl: './action-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionItemComponent implements OnInit, OnDestroy {
    action = input.required<DeviceAction>();

    readonly showDialog = signal(false);
    readonly cancelling = signal(false);
    readonly currentTime = signal(Date.now());

    private messageService = inject(MessageService);
    private apiService = inject(CloudApiService);
    private updateInterval?: number;

    stages = computed<StageState[]>(() => {
        // Trigger recompute when currentTime changes
        this.currentTime();
        const action = this.action();
        return [
            this.getCreatedStage(action),
            this.getScheduledStage(action),
            this.getDispatchedStage(action),
            this.getResultStage(action)
        ];
    });

    textLabels = computed<StageState[]>(() => {
        // Trigger recompute when currentTime changes
        this.currentTime();
        const action = this.action();
        return [
            this.getCreatedScheduledLabel(action),
            this.getDispatchedLabel(action),
            this.getResultLabel(action)
        ];
    });

    ngOnInit(): void {
        // Update current time every second to trigger recompute of relative times
        this.updateInterval = window.setInterval(() => {
            this.currentTime.set(Date.now());
        }, 1000);
    }

    ngOnDestroy(): void {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
    }

    getActionIcon(actionType: DeviceActionType): string {
        switch (actionType) {
            case 'Reboot': return 'pi-refresh';
            case 'Restart': return 'pi-replay';
            case 'Update': return 'pi-download';
            case 'FirmwareUpdate': return 'pi-microchip';
            case 'MigrateCloud': return 'pi-cloud-upload';
            default: return 'pi-cog';
        }
    }

    private getCreatedStage(action: DeviceAction): StageState {
        return {
            icon: 'pi-check',
            label: 'Created',
            details: this.formatDate(action.createdUtc),
            state: 'completed',
            color: 'success'
        };
    }

    private getScheduledStage(action: DeviceAction): StageState {
        const isScheduled = !!action.scheduledForUtc;
        const isPending = action.status === 'Pending';

        // If cancelled while pending (before dispatch), show cancel icon here
        if (action.status === 'Cancelled' && !action.dispatchedUtc) {
            return {
                icon: 'pi-ban',
                label: 'Cancelled',
                details: this.formatDate(action.completedUtc),
                state: 'completed',
                color: 'error'
            };
        }

        if (isPending) {
            return {
                icon: '',
                label: 'Scheduled',
                details: isScheduled ? this.formatDate(action.scheduledForUtc) : 'Active immediately',
                state: 'active',
                color: 'warning'
            };
        } else {
            return {
                icon: 'pi-check',
                label: 'Scheduled',
                details: isScheduled ? this.formatDate(action.scheduledForUtc) : 'Immediate',
                state: 'completed',
                color: 'success'
            };
        }
    }

    private getDispatchedStage(action: DeviceAction): StageState {
        // If cancelled while dispatched, show cancel icon here
        if (action.status === 'Cancelled' && action.dispatchedUtc) {
            return {
                icon: 'pi-ban',
                label: 'Cancelled',
                details: this.formatDate(action.completedUtc),
                state: 'completed',
                color: 'warning'
            };
        }

        // If cancelled before dispatch, this stage should be pending
        if (action.status === 'Cancelled' && !action.dispatchedUtc) {
            return {
                icon: '',
                label: 'Dispatched',
                details: 'Awaiting dispatch',
                state: 'pending'
            };
        }

        if (action.status === 'Pending') {
            return {
                icon: '',
                label: 'Dispatched',
                details: 'Awaiting dispatch',
                state: 'pending'
            };
        } else if (action.status === 'Dispatched') {
            return {
                icon: '',
                label: 'Dispatched',
                details: action.dispatchedUtc ? this.formatDate(action.dispatchedUtc) : 'In progress',
                state: 'active',
                color: 'warning'
            };
        } else {
            return {
                icon: 'pi-check',
                label: 'Dispatched',
                details: this.formatDate(action.dispatchedUtc),
                state: 'completed',
                color: 'success'
            };
        }
    }

    private getResultStage(action: DeviceAction): StageState {
        switch (action.status) {
            case 'Succeeded':
                return {
                    icon: 'pi-check',
                    label: 'Succeeded',
                    details: this.formatDate(action.completedUtc),
                    state: 'completed',
                    color: 'success'
                };
            case 'Failed':
                return {
                    icon: 'pi-times',
                    label: 'Failed',
                    details: this.formatDate(action.completedUtc),
                    state: 'completed',
                    color: 'error'
                };
            case 'Cancelled':
                // Cancelled actions show the icon at the stage where cancellation occurred
                // So this result stage should just be pending
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Cancelled',
                    state: 'pending'
                };
            case 'Dispatched':
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Awaiting result',
                    state: 'pending'
                };
            case 'Pending':
            default:
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Awaiting result',
                    state: 'pending'
                };
        }
    }

    private getCreatedScheduledLabel(action: DeviceAction): StageState {
        const isScheduled = !!action.scheduledForUtc;
        const isPending = action.status === 'Pending';

        // If cancelled while pending (before dispatch), show label as cancelled here
        if (action.status === 'Cancelled' && !action.dispatchedUtc) {
            return {
                icon: '',
                label: 'Cancelled',
                details: this.formatSmartTime(action.completedUtc),
                state: 'completed',
                color: 'warning'
            };
        }

        if (isPending) {
            return {
                icon: '',
                label: isScheduled ? 'Scheduled' : 'Created pending dispatch',
                details: isScheduled ? this.formatSmartTime(action.scheduledForUtc) : this.formatSmartTime(action.createdUtc),
                state: 'active',
                color: 'warning'
            };
        } else {
            return {
                icon: '',
                label: isScheduled ? 'Scheduled' : 'Created',
                details: isScheduled ? this.formatSmartTime(action.scheduledForUtc) : this.formatSmartTime(action.createdUtc),
                state: 'completed',
                color: 'success'
            };
        }
    }

    private getDispatchedLabel(action: DeviceAction): StageState {
        // If cancelled while dispatched, show label as cancelled here
        if (action.status === 'Cancelled' && action.dispatchedUtc) {
            return {
                icon: '',
                label: 'Cancelled',
                details: this.formatSmartTime(action.completedUtc),
                state: 'completed',
                color: 'warning'
            };
        }

        // If cancelled before dispatch, this label should be pending
        if (action.status === 'Cancelled' && !action.dispatchedUtc) {
            return {
                icon: '',
                label: 'Dispatched',
                details: 'Awaiting dispatch',
                state: 'pending'
            };
        }

        if (action.status === 'Pending') {
            return {
                icon: '',
                label: 'Dispatched',
                details: 'Awaiting dispatch',
                state: 'pending'
            };
        } else if (action.status === 'Dispatched') {
            return {
                icon: '',
                label: 'Dispatched',
                details: action.dispatchedUtc ? this.formatSmartTime(action.dispatchedUtc) : 'In progress',
                state: 'active',
                color: 'warning'
            };
        } else {
            return {
                icon: '',
                label: 'Dispatched',
                details: this.formatSmartTime(action.dispatchedUtc),
                state: 'completed',
                color: 'success'
            };
        }
    }

    private getResultLabel(action: DeviceAction): StageState {
        switch (action.status) {
            case 'Succeeded':
                return {
                    icon: '',
                    label: 'Succeeded',
                    details: this.formatSmartTime(action.completedUtc),
                    state: 'completed',
                    color: 'success'
                };
            case 'Failed':
                return {
                    icon: '',
                    label: 'Failed',
                    details: this.formatSmartTime(action.completedUtc),
                    state: 'completed',
                    color: 'error'
                };
            case 'Cancelled':
                // Cancelled actions show the label at the stage where cancellation occurred
                // So this result label should just be pending
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Cancelled',
                    state: 'pending'
                };
            case 'Dispatched':
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Awaiting result',
                    state: 'pending'
                };
            case 'Pending':
            default:
                return {
                    icon: '',
                    label: 'Result',
                    details: 'Awaiting result',
                    state: 'pending'
                };
        }
    }

    formatDate(value?: string | Date | null): string {
        if (!value) return '—';
        try {
            const dt = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(dt.getTime())) return typeof value === 'string' ? value : '—';
            return dt.toLocaleString();
        } catch {
            return typeof value === 'string' ? value : '—';
        }
    }

    // Smart time formatter based on requirements:
    // - Today: relative time
    // - Future: time and date
    formatSmartTime(value?: string | Date | null): string {
        if (!value) return '—';
        try {
            const dt = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(dt.getTime())) return typeof value === 'string' ? value : '—';

            const now = new Date();
            const diff = dt.getTime() - now.getTime();
            const isToday = dt.toDateString() === now.toDateString();
            const isFuture = diff > 0;

            // If it's today (past or present), show relative time
            if (isToday) {
                return this.formatRelative(dt);
            }

            // If it's in the future, show date and time
            if (isFuture) {
                return dt.toLocaleString();
            }

            // If it's in the past (not today), show relative time
            return this.formatRelative(dt);
        } catch {
            return typeof value === 'string' ? value : '—';
        }
    }

    // Get the last updated time for an action (most recent of created/dispatched/completed)
    getLastUpdatedTime(action: DeviceAction): string {
        // Trigger recompute when currentTime changes
        this.currentTime();

        const times = [
            action.completedUtc,
            action.dispatchedUtc,
            action.createdUtc
        ].filter((t): t is string => t != null);

        if (times.length === 0) return '—';

        // Find the most recent time
        const latestTime = times.reduce((latest, current) => {
            const latestDate = new Date(latest);
            const currentDate = new Date(current);
            return currentDate > latestDate ? current : latest;
        });

        return this.formatRelative(latestTime);
    }

    formatRelative(value?: string | Date | null): string {
        if (!value) return '';
        const dt = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(dt.getTime())) return '';
        const diff = dt.getTime() - Date.now();
        const future = diff >= 0;
        const absSeconds = Math.floor(Math.abs(diff) / 1000);
        const format = (value: number, singular: string, plural: string) => `${future ? 'in ' : ''}${value} ${value === 1 ? singular : plural}${future ? '' : ' ago'}`;
        if (absSeconds < 60) return format(Math.max(absSeconds, 1), 'second', 'seconds');
        const minutes = Math.floor(absSeconds / 60);
        if (minutes < 60) return format(minutes, 'minute', 'minutes');
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return format(hours, 'hour', 'hours');
        const days = Math.floor(hours / 24);
        if (days < 7) return format(days, 'day', 'days');
        const weeks = Math.floor(days / 7);
        if (weeks < 5) return format(weeks, 'week', 'weeks');
        const months = Math.floor(days / 30);
        if (months < 12) return format(months, 'month', 'months');
        const years = Math.floor(days / 365);
        return format(years, 'year', 'years');
    }

    formatPayload(action: DeviceAction): string {
        if (!action.payload) return '';
        try {
            const payload = { ...action.payload };
            // Never render namespace API keys (present on MigrateCloud payloads)
            if (typeof payload.apiKey === 'string' && payload.apiKey.length > 0) {
                payload.apiKey = `${payload.apiKey.slice(0, 6)}…****`;
            }
            return JSON.stringify(payload, null, 2);
        } catch (err) {
            return String(action.payload);
        }
    }

    canCancelAction(): boolean {
        const status = this.action().status;
        return !this.cancelling() && (status === 'Pending' || status === 'Dispatched');
    }

    getConnectorClass(toIndex: number): 'completed' | 'cancelled' | 'default' {
        const action = this.action();
        const stages = this.stages();
        const fromStage = stages[toIndex - 1];
        const toStage = stages[toIndex];
        
        // For cancelled actions, show red line to/from the cancellation point
        if (action.status === 'Cancelled') {
            // If connecting to a cancelled stage or from a cancelled stage
            if (toStage.color === 'warning' || fromStage.color === 'warning') {
                return 'cancelled';
            }
        }
        
        // Green line only if both dots are completed successfully (green)
        if (fromStage.state === 'completed' && toStage.state === 'completed' &&
            fromStage.color === 'success' && toStage.color === 'success') {
            return 'completed';
        }
        
        // Default grey for everything else
        return 'default';
    }

    showResultDialog(): void {
        const status = this.action().status;
        // Only show dialog for final result states (not for cancelled at intermediate stages)
        if (status === 'Failed' || status === 'Succeeded') {
            this.showDialog.set(true);
        }
    }

    showCancelledDialog(): void {
        // Allow clicking on the cancelled stage to show dialog
        if (this.action().status === 'Cancelled') {
            this.showDialog.set(true);
        }
    }

    async cancelAction(): Promise<void> {
        const action = this.action();

        // Set cancelling state
        this.cancelling.set(true);

        try {
            await this.apiService.cancelDeviceAction(action.deviceId, action.id);

            this.messageService.add({
                severity: 'success',
                summary: 'Action Cancelled',
                detail: 'The action has been successfully cancelled.',
                life: 3000
            });
        } catch (err: any) {
            this.messageService.add({
                severity: 'error',
                summary: 'Cancellation Failed',
                detail: err.error?.message || err.message || 'Failed to cancel the action. The action may have already completed or failed.',
                life: 5000
            });
        } finally {
            this.cancelling.set(false);
        }
    }
}
